import React, { useMemo, useRef, useState } from "react";
import { api, type AudioAsset, type VoiceoverConfig } from "../../api";
import { AudioLibrary } from "./AudioLibrary";
import { MicRecorder } from "./MicRecorder";
import type { StopResult } from "../../lib/voiceoverRecorder";

type Props = {
  value: VoiceoverConfig | undefined;
  onChange: (next: VoiceoverConfig | undefined) => void;
  /** Reel total length — drives the default bar duration when adding a VO. */
  reelDurationSec: number;
  /** Current playhead — used as default fromSec when adding voiceover. */
  playheadSec?: number;
  /** Wird vom Composer geliefert: schneidet das aktuelle VO-Segment am
   *  Playhead in zwei Stücke. Der Composer kümmert sich um die Segment-
   *  Mathe — hier triggern wir nur den Aufruf via Button. */
  onSplitAtPlayhead?: () => void;
  /** Anzahl Schnitt-Stücke aktuell — fürs UI-Hinweis-Label. */
  segmentCount?: number;
  /** Persistierte User-Präferenz: spielt der Player automatisch ab,
   *  während aufgenommen wird? */
  recordPlayPreview?: boolean;
  onRecordPlayPreviewToggle?: (next: boolean) => void;
  /** Composer-Hooks fürs Recording — Player-Steuerung + Result-Handling. */
  onPlayPreview?: () => void;
  onPausePreview?: () => void;
  onRecordingFinished?: (res: StopResult) => Promise<void> | void;
  /** Export der aktuellen VO-Datei als WAV oder MP3. Composer kümmert
   *  sich um Decode + Re-Encode + Download-Trigger. */
  onExportVoiceover?: (format: "wav" | "mp3", srcUrl?: string) => Promise<void> | void;
};

const DEFAULT_VOLUME = 1.0;

/**
 * Voiceover-Panel — analog zum MusicPanel, aber ohne mitgelieferte Beds:
 * der User lädt seine eigene Sprachaufnahme hoch (oder wählt eine bereits
 * hochgeladene Datei aus der Audio-Library) und kann sie anschließend
 * schneiden, positionieren und faden.
 *
 * Features:
 *  - Datei-Upload direkt aus dem Panel (Drag&Drop oder Klick).
 *  - Kategorie-Filter „voiceover" (mit Fallback auf alle Audios).
 *  - Quell-Dauer wird vom HTMLAudioElement geprobed (siehe AudioLibrary).
 *  - Trim-Start (Schnitt am Anfang der Originaldatei),
 *    Bar-Position + Bar-Länge (sichtbares Fenster auf der Timeline),
 *    Fade-In / Fade-Out, Lautstärke + globaler Mute.
 *  - „An Playhead" / „An Anfang" / „Volle Länge" Quick-Actions —
 *    identisch zum Music-Panel, damit das UX konsistent bleibt.
 */
export const VoiceoverPanel: React.FC<Props> = ({
  value,
  onChange,
  reelDurationSec,
  playheadSec = 0,
  onSplitAtPlayhead,
  segmentCount = 0,
  recordPlayPreview = true,
  onRecordPlayPreviewToggle,
  onPlayPreview,
  onPausePreview,
  onRecordingFinished,
  onExportVoiceover,
}) => {
  const enabled = !!value && value.enabled && !!value.src;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const sourceLabel = useMemo(() => {
    if (!value) return null;
    if (value.label) return value.label;
    if (value.src) return "Voiceover";
    return null;
  }, [value]);

  const useAsset = (asset: AudioAsset): void => {
    const sourceDur = asset.durationSec > 0 ? asset.durationSec : reelDurationSec;
    const barDur = Math.min(sourceDur, Math.max(reelDurationSec, sourceDur));
    onChange({
      src: asset.url,
      assetId: asset.id,
      label: asset.displayName,
      volume: value?.volume ?? DEFAULT_VOLUME,
      enabled: true,
      fromSec: value?.fromSec ?? Math.max(0, playheadSec),
      durSec: barDur,
      trimStartSec: 0,
      fadeInSec: value?.fadeInSec ?? 0,
      fadeOutSec: value?.fadeOutSec ?? 0,
    });
  };

  /** Direkter Upload aus dem Panel — die Datei landet in der Audio-Library
   *  als „voiceover"-Asset und wird sofort als aktive VO-Spur gesetzt. */
  const handleUpload = async (files: FileList | File[] | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setErr(null);
    try {
      const r = await api.audioUpload(files, "voiceover");
      // Refresh die eingebettete AudioLibrary, damit die neue Datei
      // sofort in der Liste auftaucht.
      setRefreshKey((k) => k + 1);
      const first = r.uploaded[0];
      if (first) useAsset(first);
    } catch (e) {
      setErr(`Upload fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  const patch = (p: Partial<VoiceoverConfig>): void => {
    if (!value) return;
    onChange({ ...value, ...p });
  };

  const toggleEnabled = (): void => {
    if (!value) return; // Kein Toggle ohne hochgeladene Datei.
    onChange({ ...value, enabled: !value.enabled });
  };

  const clear = (): void => onChange(undefined);

  const totalSec = Math.max(0.1, reelDurationSec);
  const fromSec = value?.fromSec ?? 0;
  const durSec = value?.durSec ?? Math.max(0.5, totalSec - fromSec);

  return (
    <div className="side-panel voiceover-panel">
      <div className="side-panel-head">
        <h4>🎙️ Voiceover</h4>
        {value && (
          <button
            type="button"
            className={`toggle ${enabled ? "on" : "off"}`}
            onClick={toggleEnabled}
            aria-pressed={enabled}
            title={
              enabled
                ? "Voiceover stumm schalten — Spur bleibt erhalten"
                : "Voiceover aktivieren"
            }
          >
            <span className="dot" />
            <span className="lbl">{enabled ? "An" : "Aus"}</span>
          </button>
        )}
      </div>

      <p className="muted" style={{ fontSize: 11.5, margin: "0 0 10px", lineHeight: 1.45 }}>
        Direkt mit dem Mikrofon aufnehmen oder eine fertige Sprachdatei
        hochladen (mp3, wav, m4a, ogg, flac …). Beim Rendern wird die Spur
        als eigene Audiospur ins MP4 gemischt.
      </p>

      {onRecordingFinished && onPlayPreview && onPausePreview && (
        <MicRecorder
          playheadSec={playheadSec}
          autoPlayWhilstRecording={recordPlayPreview}
          onAutoPlayToggle={(v) => onRecordPlayPreviewToggle?.(v)}
          onPlayPreview={onPlayPreview}
          onPausePreview={onPausePreview}
          onRecordingFinished={onRecordingFinished}
          hasExistingVoiceover={!!value?.src}
        />
      )}

      <div className="voiceover-upload-row">
        <button
          type="button"
          className="btn primary sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Lädt…" : value?.src ? "Andere Datei wählen" : "+ Voiceover hochladen"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.oga,.flac,.webm"
          style={{ display: "none" }}
          onChange={(e) => {
            void handleUpload(e.target.files);
            e.target.value = "";
          }}
        />
        {value?.src && (
          <button type="button" className="btn ghost sm" onClick={clear}>
            Spur entfernen
          </button>
        )}
      </div>

      {err && <div className="audio-library-error" style={{ marginTop: 8 }}>{err}</div>}

      <div className="voiceover-section-label" style={{ marginTop: 14 }}>
        Library
      </div>
      <AudioLibrary
        kindFilter="voiceover"
        defaultKind="voiceover"
        compact
        refreshKey={refreshKey}
        onSelect={(asset) => useAsset(asset)}
        dragMime={null}
      />

      {value?.src && (
        <>
          <div className="music-active-card" style={{ marginTop: 14 }}>
            <div className="music-active-title">🎙️ {sourceLabel}</div>
            <div className="music-active-sub">
              {fromSec.toFixed(1)}s → {(fromSec + durSec).toFixed(1)}s
              {value.trimStartSec ? ` · Schnitt ab ${value.trimStartSec.toFixed(1)}s` : ""}
            </div>
          </div>

          <label className="form-row">
            <span>Lautstärke ({Math.round((value.volume ?? DEFAULT_VOLUME) * 100)}%)</span>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round((value.volume ?? DEFAULT_VOLUME) * 100)}
              onChange={(e) => patch({ volume: Number(e.target.value) / 100 })}
            />
          </label>

          <div className="form-row form-row-pair">
            <label>
              <span>Start auf Timeline (s)</span>
              <input
                type="number"
                step={0.1}
                min={0}
                value={Number(fromSec.toFixed(2))}
                onChange={(e) =>
                  patch({ fromSec: Math.max(0, Number(e.target.value)) })
                }
              />
            </label>
            <label>
              <span>Bar-Länge (s)</span>
              <input
                type="number"
                step={0.1}
                min={0.2}
                value={Number(durSec.toFixed(2))}
                onChange={(e) => patch({ durSec: Math.max(0.2, Number(e.target.value)) })}
              />
            </label>
          </div>

          <label className="form-row">
            <span>
              Schnitt am Anfang ({(value.trimStartSec ?? 0).toFixed(1)}s) — Wo in der
              Originaldatei beginnt die Wiedergabe?
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(0.1, durSec * 4)}
              step={0.1}
              value={value.trimStartSec ?? 0}
              onChange={(e) =>
                patch({ trimStartSec: Math.max(0, Number(e.target.value)) })
              }
            />
          </label>

          <div className="form-row form-row-pair">
            <label>
              <span>Fade-In ({(value.fadeInSec ?? 0).toFixed(1)}s)</span>
              <input
                type="range"
                min={0}
                max={Math.min(8, durSec / 2)}
                step={0.1}
                value={value.fadeInSec ?? 0}
                onChange={(e) =>
                  patch({ fadeInSec: Math.max(0, Number(e.target.value)) })
                }
              />
            </label>
            <label>
              <span>Fade-Out ({(value.fadeOutSec ?? 0).toFixed(1)}s)</span>
              <input
                type="range"
                min={0}
                max={Math.min(8, durSec / 2)}
                step={0.1}
                value={value.fadeOutSec ?? 0}
                onChange={(e) =>
                  patch({ fadeOutSec: Math.max(0, Number(e.target.value)) })
                }
              />
            </label>
          </div>

          <div className="music-quick-actions">
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => patch({ fromSec: Math.max(0, playheadSec) })}
              title="Start auf aktuelle Playhead-Position setzen"
            >
              ↦ An Playhead
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => patch({ fromSec: 0 })}
              title="Voiceover ganz an den Anfang ziehen"
            >
              ⇤ An Anfang
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => patch({ trimStartSec: 0 })}
              title="Schnitt am Anfang aufheben — Wiedergabe ab Sekunde 0 der Datei"
            >
              ↺ Schnitt zurücksetzen
            </button>
          </div>

          {onSplitAtPlayhead && (
            <div className="vo-split-row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn primary sm"
                onClick={onSplitAtPlayhead}
                title="Voiceover am Playhead in zwei Stücke schneiden (Tastenkürzel: S)"
              >
                ✂ Bei Playhead schneiden
              </button>
              <span className="muted vo-split-hint">
                {segmentCount > 1
                  ? `${segmentCount} Schnitt-Stücke · einzeln klick- + ⌫-bar in der Timeline`
                  : "Tipp: Playhead positionieren + S — splittet die Spur in zwei Teile"}
              </span>
            </div>
          )}

          {onExportVoiceover && (
            <div className="vo-export-row" style={{ marginTop: 12 }}>
              <span className="vo-export-label muted">Export für externen Schnitt</span>
              <div className="vo-export-actions">
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => void onExportVoiceover("wav")}
                  title="Aktuelle Voiceover-Datei als verlustfreies WAV herunterladen"
                >
                  ⬇ WAV
                </button>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => void onExportVoiceover("mp3")}
                  title="Aktuelle Voiceover-Datei als 192-kbps-MP3 herunterladen"
                >
                  ⬇ MP3
                </button>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => fileInputRef.current?.click()}
                  title="Modifizierte Datei wieder hochladen — ersetzt die aktive Voiceover-Datei"
                >
                  ↻ Bearbeitete Datei reinladen
                </button>
              </div>
              <span className="muted vo-export-hint">
                Workflow: WAV/MP3 exportieren → in deinem Tool (Audacity, Logic, …)
                cleanen / EQ-en → mit „Reinladen" wieder hochladen.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
